import React from 'react';

/*
 * NANOVRAM brand assets — logo mark + banner.
 * The wordmark is drawn with a hand-made 5x7 pixel font (letters N,A,O,V,R,M)
 * so exports to PNG never depend on webfonts.
 */

const GLYPHS = {
  N: ['X...X', 'XX..X', 'X.X.X', 'X..XX', 'X...X', 'X...X', 'X...X'],
  A: ['.XXX.', 'X...X', 'X...X', 'XXXXX', 'X...X', 'X...X', 'X...X'],
  O: ['.XXX.', 'X...X', 'X...X', 'X...X', 'X...X', 'X...X', '.XXX.'],
  V: ['X...X', 'X...X', 'X...X', 'X...X', 'X...X', '.X.X.', '..X..'],
  R: ['XXXX.', 'X...X', 'X...X', 'XXXX.', 'X.X..', 'X..X.', 'X...X'],
  M: ['X...X', 'XX.XX', 'X.X.X', 'X.X.X', 'X...X', 'X...X', 'X...X'],
};

export const PixelWord = ({ word, x = 0, y = 0, px = 6, color = '#f7f0e1', gap = 2, shadow = null }) => {
  const cells = [];
  let cx = x;
  for (const ch of word) {
    const g = GLYPHS[ch];
    if (!g) { cx += px * 3; continue; }
    g.forEach((row, ry) => {
      [...row].forEach((c, rx) => {
        if (c === 'X') {
          if (shadow) cells.push(<rect key={`s${cx}-${rx}-${ry}`} x={cx + rx * px + px * 0.6} y={y + ry * px + px * 0.6} width={px} height={px} fill={shadow} />);
        }
      });
    });
    cx += px * (5 + gap);
  }
  cx = x;
  for (const ch of word) {
    const g = GLYPHS[ch];
    if (!g) { cx += px * 3; continue; }
    g.forEach((row, ry) => {
      [...row].forEach((c, rx) => {
        if (c === 'X') cells.push(<rect key={`${cx}-${rx}-${ry}`} x={cx + rx * px} y={y + ry * px} width={px} height={px} fill={color} />);
      });
    });
    cx += px * (5 + gap);
  }
  return <g shapeRendering="crispEdges">{cells}</g>;
};

export const wordWidth = (word, px = 6, gap = 2) => word.length * px * (5 + gap) - px * gap;

// ---------- the mark: a gold RAM stick with a teal spark ----------
export const NVLogo = ({ size = 40, id = 'nv' }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" style={{ imageRendering: 'pixelated' }} aria-label="NANOVRAM mark" shapeRendering="crispEdges">
    {/* stick body */}
    <rect x="4" y="8" width="32" height="22" fill="#eac54f" stroke="#2a1a0e" strokeWidth="2.5" />
    {/* notch */}
    <rect x="16" y="27" width="8" height="5" fill="#211510" stroke="#2a1a0e" strokeWidth="2" />
    {/* chips */}
    <rect x="8" y="12" width="7" height="9" fill="#5c3b1e" stroke="#2a1a0e" strokeWidth="1.6" />
    <rect x="17" y="12" width="7" height="9" fill="#5c3b1e" stroke="#2a1a0e" strokeWidth="1.6" />
    <rect x="26" y="12" width="6" height="9" fill="#5c3b1e" stroke="#2a1a0e" strokeWidth="1.6" />
    {/* pins */}
    {[7, 12, 17, 22, 27, 32].map((x) => (
      <rect key={x} x={x} y="30" width="2.6" height="5" fill="#d9c6a5" stroke="#2a1a0e" strokeWidth="1" />
    ))}
    {/* teal spark, top-right */}
    <rect x="29" y="2" width="4" height="4" fill="#2f8f85" />
    <rect x="33" y="6" width="4" height="4" fill="#2f8f85" />
    <rect x="25" y="6" width="4" height="4" fill="#7fd6cb" />
    <rect x="29" y="10" width="4" height="4" fill="#7fd6cb" />
  </svg>
);

// ---------- square avatar tile (logo on night ground) ----------
export const NVAvatar = ({ size = 160 }) => (
  <svg width={size} height={size} viewBox="0 0 160 160" shapeRendering="crispEdges" aria-label="NANOVRAM avatar">
    <rect width="160" height="160" fill="#211510" />
    {[[14, 22], [130, 14], [40, 132], [118, 122], [78, 18], [22, 84], [142, 74]].map(([x, y], i) => (
      <rect key={`star-${x}-${y}`} x={x} y={y} width="3" height="3" fill="#f7f0e1" opacity={i % 2 ? 0.5 : 0.85} />
    ))}
    <g transform="translate(28,26) scale(2.6)">
      <rect x="4" y="8" width="32" height="22" fill="#eac54f" stroke="#2a1a0e" strokeWidth="2.5" />
      <rect x="16" y="27" width="8" height="5" fill="#211510" stroke="#2a1a0e" strokeWidth="2" />
      <rect x="8" y="12" width="7" height="9" fill="#5c3b1e" stroke="#2a1a0e" strokeWidth="1.6" />
      <rect x="17" y="12" width="7" height="9" fill="#5c3b1e" stroke="#2a1a0e" strokeWidth="1.6" />
      <rect x="26" y="12" width="6" height="9" fill="#5c3b1e" stroke="#2a1a0e" strokeWidth="1.6" />
      {[7, 12, 17, 22, 27, 32].map((x) => (
        <rect key={x} x={x} y="30" width="2.6" height="5" fill="#d9c6a5" stroke="#2a1a0e" strokeWidth="1" />
      ))}
      <rect x="29" y="2" width="4" height="4" fill="#2f8f85" />
      <rect x="33" y="6" width="4" height="4" fill="#2f8f85" />
      <rect x="25" y="6" width="4" height="4" fill="#7fd6cb" />
      <rect x="29" y="10" width="4" height="4" fill="#7fd6cb" />
    </g>
    <g transform="translate(0,124)">
      <PixelWord word="NANO" x={14} y={0} px={2.4} color="#f7f0e1" />
      <PixelWord word="VRAM" x={14 + wordWidth('NANO', 2.4) + 6} y={0} px={2.4} color="#eac54f" />
    </g>
  </svg>
);

// ---------- banner 1500x500 (X/Twitter header size) ----------
export const NVBanner = ({ width = '100%' }) => {
  const px = 14;
  const nanoW = wordWidth('NANO', px);
  return (
    <svg viewBox="0 0 1500 500" style={{ width, display: 'block' }} shapeRendering="crispEdges" aria-label="NANOVRAM banner">
      <rect width="1500" height="500" fill="#211510" />
      {/* stars */}
      {[[80, 60], [220, 140], [400, 40], [640, 90], [900, 50], [1120, 120], [1350, 70], [1440, 200],
        [160, 380], [520, 430], [820, 400], [1060, 440], [1260, 380], [60, 250], [1420, 320], [700, 300]].map(([x, y], i) => (
        <rect key={`star-${x}-${y}`} x={x} y={y} width="4" height="4" fill="#f7f0e1" opacity={i % 3 === 0 ? 0.9 : 0.45} />
      ))}
      {/* ground strip */}
      <rect x="0" y="452" width="1500" height="48" fill="#8a5a33" />
      <rect x="0" y="446" width="1500" height="6" fill="#2a1a0e" />
      {/* tape strip */}
      {Array.from({ length: 50 }).map((_, i) => (
        <rect key={`tape-${i * 30}`} x={i * 30} y="0" width="15" height="10" fill={i % 2 ? '#2a1a0e' : '#eac54f'} />
      ))}
      {/* logo mark */}
      <g transform="translate(120,120) scale(5.6)">
        <rect x="4" y="8" width="32" height="22" fill="#eac54f" stroke="#2a1a0e" strokeWidth="2.5" />
        <rect x="16" y="27" width="8" height="5" fill="#211510" stroke="#2a1a0e" strokeWidth="2" />
        <rect x="8" y="12" width="7" height="9" fill="#5c3b1e" stroke="#2a1a0e" strokeWidth="1.6" />
        <rect x="17" y="12" width="7" height="9" fill="#5c3b1e" stroke="#2a1a0e" strokeWidth="1.6" />
        <rect x="26" y="12" width="6" height="9" fill="#5c3b1e" stroke="#2a1a0e" strokeWidth="1.6" />
        {[7, 12, 17, 22, 27, 32].map((x) => (
          <rect key={x} x={x} y="30" width="2.6" height="5" fill="#d9c6a5" stroke="#2a1a0e" strokeWidth="1" />
        ))}
        <rect x="29" y="2" width="4" height="4" fill="#2f8f85" />
        <rect x="33" y="6" width="4" height="4" fill="#2f8f85" />
        <rect x="25" y="6" width="4" height="4" fill="#7fd6cb" />
        <rect x="29" y="10" width="4" height="4" fill="#7fd6cb" />
      </g>
      {/* wordmark */}
      <g transform="translate(0,170)">
        <PixelWord word="NANO" x={430} y={0} px={px} color="#f7f0e1" shadow="rgba(0,0,0,.55)" />
        <PixelWord word="VRAM" x={430 + nanoW + 26} y={0} px={px} color="#eac54f" shadow="rgba(0,0,0,.55)" />
      </g>
      {/* tagline — system monospace so PNG export never breaks */}
      <text x="434" y="330" fill="#d9c6a5" fontFamily="monospace" fontSize="30" letterSpacing="6">
        HOURLY GPU CORES, SETTLED IN ETH
      </text>
      <text x="434" y="380" fill="#2f8f85" fontFamily="monospace" fontSize="24" letterSpacing="4">
        $NVRM · NO SIGNAL, NEVER ZERO
      </text>
    </svg>
  );
};

// ---------- export helper: SVG node -> PNG download ----------
export const downloadSvgAsPng = (svgEl, filename, outW, outH) => {
  const xml = new XMLSerializer().serializeToString(svgEl);
  const blob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, 0, 0, outW, outH);
    URL.revokeObjectURL(url);
    canvas.toBlob((png) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(png);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    }, 'image/png');
  };
  img.src = url;
};
